"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Search, Tag, ArrowRight, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";

export interface BlogPost {
  id: string;
  title: string;
  summary: string;
  category: string;
  tags: string[];
  author: string;
  authorImage: string;
  published: string;
  image: string;
  slug: string;
}

interface AnimatedBlogCardProps {
  posts: BlogPost[];
  className?: string;
}

const AnimatedBlogCard: React.FC<AnimatedBlogCardProps> = ({ posts, className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(posts);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract all unique categories from posts
  const allCategories = ["All", ...Array.from(
    new Set(posts.map((post) => post.category))
  ).sort()];

  // Handle search and filter
  useEffect(() => {
    const filtered = posts.filter((post) => {
      const matchesSearch =
        searchTerm === "" ||
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFilter =
        activeFilter === "All" || post.category === activeFilter;

      return matchesSearch && matchesFilter;
    });

    setFilteredPosts(filtered);
  }, [searchTerm, activeFilter, posts]);

  // Handle scroll progress
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      const scrolled = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const clearFilters = () => {
    setActiveFilter("All");
    setSearchTerm("");
  };

  return (
    <div ref={containerRef} className={cn("relative w-full py-12 px-4 md:px-8 lg:px-12", className)}>
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-800 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-600"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: "0%" }}
          animate={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      {/* Search and Filter Section */}
      <div className="mb-12 space-y-6">
        <div className="relative max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-[color,box-shadow] duration-200"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          {allCategories.map((category) => (
            <motion.button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                activeFilter === category
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                  : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              } hover:shadow-md transition-[color,box-shadow] duration-200`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <Tag className="mr-1.5 h-3.5 w-3.5" />
              {category}
            </motion.button>
          ))}
          {(activeFilter !== "All" || searchTerm) && (
            <motion.button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:shadow-md transition-[color,box-shadow] duration-200"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear filters
            </motion.button>
          )}
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPosts.length > 0 ? (
          filteredPosts.map((post) => (
            <motion.div
              key={post.id}
              className="group bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-[box-shadow] duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              whileHover={{ y: -5 }}
            >
              <Link href={`/blog/${post.slug}`} className="block">
                <div className="relative overflow-hidden aspect-video">
                  <Image
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    width={600}
                    height={400}
                  />
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-500/80 text-white backdrop-blur-sm">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold tracking-tight line-clamp-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 line-clamp-3">
                    {post.summary}
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span
                        key={`${post.id}-${tag}`}
                        className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                      >
                        {tag}
                      </span>
                    ))}
                    {post.tags.length > 3 && (
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                        +{post.tags.length - 3} more
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center">
                      <div className="relative h-8 w-8 rounded-full overflow-hidden mr-2">
                        <Image
                          src={post.authorImage}
                          alt={post.author}
                          className="object-cover"
                          fill
                        />
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        <span>{post.author}</span>
                        <div className="text-xs text-gray-400">{post.published}</div>
                      </div>
                    </div>
                    <span
                      className="inline-flex items-center text-emerald-600 dark:text-emerald-400 font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 rounded-md transition-[color,box-shadow]"
                    >
                      Read more
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-lg text-gray-600 dark:text-gray-400">
              No posts found matching your criteria. Try adjusting your filters.
            </p>
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimatedBlogCard;
